<template>
<flex-layout class="w-100" direction="column" wrap>
  <div class="row w-100 g-0 align-items-center" >
    <base-input-number
      label="ご住所"
      :required-tag="requiredTags"
      class="col-md-6 col-7"
      prepend-inner-text="〒"
      prepend-box-collor="lighterGray"
      :model-value="zipCode"
      @update:modelValue="emit('update:zipCode', $event)"
      type="integer"
      :rules="addressRules.zipCode"
      placeholder="半角数字のみ"
    />
    <base-button
      class="col-md-3 col-5 mt-1"
      block
      text-color="textLight"
      border-color="borderColor"
      color="backgroundGray"
      button-disabled-color="backgroundGray"
      size="md"
      :custom-size="50"
      :disabled="!zipCode || detectFullWidth(zipCode ? String(zipCode) : '')"
      @click="zipCodeSearch">
      <base-text no-wrap>〒検索</base-text>
    </base-button>
  </div>

  <base-select
    :model-value="pref"
    :items="[{ id: null, name: '未選択' },...prefectures]"
    :rules="addressRules.pref"
    no-label
    placeholder="都道府県を選択してください"
    :clearable="!!pref.id"
    item-value="id"
    item-text="name"
    @update:modelValue="updatePrefecture"
    return-object
  />
  <base-select
    :model-value="city"
    placeholder="市区町村を選択してください"
    :items="pref.cities"
    item-value="id"
    :clearable="!!city.id"
    no-label
    item-text="name"
    @update:modelValue="updateCity"
    return-object
    :rules="addressRules.city"
  />
  <base-select
    v-if="!hideTown"
    :model-value="town"
    return-object
    placeholder="町域を選択してください"
    no-label
    :items="towns"
    item-value="name"
    item-text="name"
    :clearable="!!town?.id"
    :disabled="townsLoading"
    @update:modelValue="updateZipCode"
    :rules="addressRules.town"
  />
  <base-input :placeholder="hideTown ? '町域・番地・ビル名など' : '番地・ビル名など'" :model-value="address"
              :rules="addressRules.address" :no-error="hideTown"
              @update:modelValue="emit('update:address', $event)"/>
  <base-text  class="ps-2" v-if="hideTown" color="error" size="xs" @click.stop>{{addressError}}</base-text>
</flex-layout>
</template>
<script setup lang="ts">
import {invalidText, useValidator} from "@/composable/ui/useValidator";
import {computed, defineEmits, nextTick, onMounted, ref, watch} from "vue";
import FlexLayout from "@/components/Layouts/FlexLayout.vue";
import BaseSelect from "@/components/Forms/BaseSelect.vue";
import BaseInput from "@/components/Forms/BaseInput.vue";
import {
  getTownByPrefIdAndCityId,
  getTownInfoListByZipCode,
  useListPrefectures
} from "@/composable/controller/useAddress";
import {Pref} from "@/domain/pref.entity";
import {City} from "@/domain/city.entity";
import BaseButton from "@/components/Buttons/BaseButton.vue";
import BaseText from "@/components/Texts/BaseText.vue";
import BaseInputNumber from "@/components/Forms/BaseInputNumber.vue";
import {Town} from "@/domain/town.entity";
import {ValidatorRule} from "@/composable/ui/useForm";

interface AddressInputPropsRules {
  tel?: ValidatorRule<string>[];
  zipCode?: ValidatorRule<string>[];
  pref?: ValidatorRule<string>[];
  city?: ValidatorRule<string>[];
  town?: ValidatorRule<string>[];
  address?: ValidatorRule<string>[];
}

const props = defineProps({
  zipCode: {
    type: String,
    required: true
  },
  pref: {
    type: Object as () => (Pref & { cities: City[] }),
    required: true
  },
  city: {
    type: Object as () => City,
    required: true
  },
  town: {
    type: Object as () => Town,
    required: false
  },
  address: {
    type: String,
    required: true
  },
  rules: {
    type: Object as () => AddressInputPropsRules,
    required: false
  },
  requiredTags: {
    type: Boolean,
    default: true
  },
  hideTown: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits([
  'update:zipCode',
  'update:pref',
  'update:city',
  'update:town',
  'update:address'
]);
const {rules} = useValidator();
const {prefectures} = useListPrefectures();
const {getTowns, townsLoading} = getTownByPrefIdAndCityId();
const towns = ref<Town[]>([]);
const { getTownInfoList } = getTownInfoListByZipCode();
const addressError = ref< "" | "この項目は必須です" | typeof invalidText >("")
const addressErrorMessage = "この項目は必須です"
const addressRules = computed<AddressInputPropsRules>(() => {
  const defaultRules: AddressInputPropsRules =  {
    tel: [rules.isPhone()],
    zipCode: [
      rules.requiredWithLabelName({name: '郵便番号'}),
      rules.min({ minLength: 7, text: '「郵便番号」を正しく入力してください。' }),
      rules.max({ maxLength: 7, text:'「郵便番号」を正しく入力してください。' }),
      rules.onlyHalfWidthNumbersWithLabelName({ name: '郵便番号' })
    ],
    pref: [rules.requiredWithLabelName({name: '都道府県'})],
    city: [rules.requiredWithLabelName({name: '市区町村'})],
    town: [rules.requiredWithLabelName({name: '町名'})],
    address: [rules.requiredWithLabelName({name: '番地・ビル名など'})],
  };
  if(!props.rules) return defaultRules;
  return {...defaultRules, ...props.rules};
})

const defaultForm = {
  zipCode: '',
  pref: { id: 0, name: '未選択', cities: [] } as (Pref & { cities: City[] }),
  city: { id: 0, name: '------', prefId: 0 } as City,
  town: { id: 0, name: '------', zipCode: '', prefId: 0, cityId: 0 } as Town,
  address: '',
};

function detectFullWidth(value: string): boolean {
  if (!value) return false;
  return [...value].some((char) => {
    const code = char.charCodeAt(0);
    return (code >= 0x2000 && code <= 0xff60) || code >= 0xffa0;
  });
}

async function updateZipCode(selectedTown?: Town) {
  emit('update:town', selectedTown || defaultForm.town);
  if (props.pref.id && props.city.id && selectedTown) {
    const filteredTown = towns.value.filter(town => town.name === selectedTown.name)[0];
    if(selectedTown) emit('update:zipCode', filteredTown.zipCode);
  }
}

async function updateCity(city?: City) {
  emit('update:town', defaultForm.town);
  if (city ) {
    emit('update:city', city || props.city);
    towns.value = await getTowns({prefId: props.pref.id, cityId: city.id });
  }
}

function updatePrefecture(prefecture?: Pref) {
  if(!prefecture || !prefecture.id) emit('update:zipCode', '');
  emit('update:pref', prefecture || defaultForm.pref);
  emit('update:city', defaultForm.city);
  emit('update:town', defaultForm.town);
}

onMounted(async ()=> {
  if (props.pref.id && props.city.id) towns.value = await getTowns({prefId: props.pref.id, cityId: props.city.id});
});

async function zipCodeSearch() {
  try {
    const result = await getTownInfoList({zipCode: String(props.zipCode)});
    towns.value = result;

    if (!result.length) {
      emit('update:town', defaultForm.town);
      return;
    }

    const prefName = result.length ? result[0].pref.name : '';
    const cityName = result.length ? result[0].city.name : '';
    const townName = result.length ? result[0].name : '';

    const prefecture = prefectures.value.find((item) => prefName === item.name);

    if (prefecture) {
      emit('update:pref', prefecture);
      const city = prefecture.cities.find((item) => cityName === item.name);

      if (city) emit('update:city', city);
      emit('update:town', towns.value.find((item) => townName === item.name) || defaultForm.town);
    }
    if(props.hideTown) {
      emit('update:address', '');
      await nextTick();
      hideAddressError();
    }
  } catch (e) {
    alert('郵便番号を確認してください。');
  }
}

watch(() => props.address, (newVal, oldVal) => {
  if (!oldVal && newVal) {
    hideAddressError();
  } else if (!newVal) {
    showAddressError();
  }
});

function showAddressError() {
  addressError.value = addressErrorMessage;
}

function showAddressInvalidError() {
  addressError.value = invalidText;
}

function hideAddressError() {
  addressError.value = '';
}

defineExpose({ showAddressError, showAddressInvalidError, hideAddressError, addressError });


</script>

<style scoped>

</style>
